/**
 * Admin role management.
 *
 * `GET /admin/roles` and the three user-role endpoints have existed since the
 * RBAC work landed, and `permissions.ts` gated a `roles` section -- but the
 * shell never carried the nav entry, so role assignment was only possible by
 * calling the API directly or by seeding.
 *
 * Reads are gated on `admin:roles:read` at the route. The grant and revoke
 * controls additionally check `admin:roles:write`, matching how the catalogue
 * screens hide their own edit controls: the server refuses either way, and this
 * keeps an operator from being offered a button that always 403s.
 */

import { Button } from "@shared-ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@shared-ui/card";
import { Input } from "@shared-ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@shared-ui/select";
import { Skeleton } from "@shared-ui/skeleton";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/ui/empty-state";
import { useAdminAuthStore } from "@/features/auth/store/admin-auth.store";
import { DataTable } from "@/features/catalog/components/data-table";
import { hasPermission } from "@/lib/permissions";
import {
	getUserRoles,
	grantRole,
	listRoles,
	revokeRole,
} from "../services/roles-api.service";

/** RFC 4122-shaped, matching the `.uuid()` check the backend validates against. */
const UUID_PATTERN =
	/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * A 400 here means the schema's `.uuid()` check rejected the id -- the only
 * case with a message worth showing verbatim-in-spirit. A 403/404/5xx/network
 * failure has nothing to do with the id's shape, so it gets a generic retry
 * message instead of implying the operator typed something wrong.
 */
function isBadRequestError(error: unknown): boolean {
	return (
		(error as { response?: { status?: number } } | null)?.response?.status ===
		400
	);
}

export function RolesDashboardPage() {
	const queryClient = useQueryClient();
	const operator = useAdminAuthStore((state) => state.operator);
	const canWrite = hasPermission(operator, "admin:roles:write");

	const [userId, setUserId] = useState("");
	const [lookedUpUserId, setLookedUpUserId] = useState("");
	const [lookupFormatError, setLookupFormatError] = useState<string | null>(
		null,
	);
	const [roleToGrant, setRoleToGrant] = useState("");

	const rolesQuery = useQuery({
		queryKey: ["admin-roles"],
		queryFn: listRoles,
	});

	const userRolesQuery = useQuery({
		queryKey: ["admin-user-roles", lookedUpUserId],
		queryFn: () => getUserRoles(lookedUpUserId),
		enabled: lookedUpUserId.length > 0,
	});

	async function refreshUserRoles() {
		await queryClient.invalidateQueries({
			queryKey: ["admin-user-roles", lookedUpUserId],
		});
	}

	const grantMutation = useMutation({
		mutationFn: (input: { roleId: string }) =>
			grantRole(lookedUpUserId, input.roleId),
		onSuccess: async () => {
			await refreshUserRoles();
			toast.success("Role granted");
			setRoleToGrant("");
		},
	});

	const revokeMutation = useMutation({
		mutationFn: (input: { roleId: string }) =>
			revokeRole(lookedUpUserId, input.roleId),
		onSuccess: async () => {
			await refreshUserRoles();
			toast.success("Role revoked");
		},
	});

	const assignment = userRolesQuery.data;
	const heldRoleIds = new Set(assignment?.roles.map((role) => role.id) ?? []);
	const grantableRoles = (rolesQuery.data ?? []).filter(
		(role) => !heldRoleIds.has(role.id),
	);

	return (
		<div className="grid gap-5">
			<div className="grid gap-2">
				<p className="text-xs font-semibold uppercase tracking-[0.16em] text-amber-700">
					Access
				</p>
				<h2 className="text-2xl font-semibold tracking-tight">Admin roles</h2>
				<p className="text-sm text-muted-foreground">
					Roles carry permissions. Granting one takes effect on the operator's
					next request.
				</p>
			</div>

			<Card className="border-slate-200 shadow-none">
				<CardHeader>
					<CardTitle>Defined roles</CardTitle>
					<CardDescription>
						Every role on the platform and the permissions it confers.
					</CardDescription>
				</CardHeader>
				<CardContent>
					{rolesQuery.isLoading ? (
						<Skeleton className="h-40 w-full rounded-2xl" />
					) : rolesQuery.data?.length ? (
						<DataTable
							rows={rolesQuery.data}
							rowKey={(role) => role.id}
							columns={[
								{
									header: "Role",
									cell: (role) => role.name,
									cellClassName: "font-medium",
								},
								{
									header: "Description",
									cell: (role) => role.description || "—",
									cellClassName: "text-muted-foreground",
								},
								{
									header: "Permissions",
									cell: (role) => (
										<div className="flex flex-wrap gap-1">
											{role.permissions.map((permission) => (
												<Badge key={permission} variant="info">
													{permission}
												</Badge>
											))}
										</div>
									),
								},
							]}
						/>
					) : (
						<EmptyState title="No admin roles are defined." />
					)}
				</CardContent>
			</Card>

			<Card className="border-slate-200 shadow-none">
				<CardHeader>
					<CardTitle>Role holders</CardTitle>
					<CardDescription>
						Look up an operator by user id to see and change what they hold.
					</CardDescription>
				</CardHeader>
				<CardContent className="grid gap-4">
					<form
						className="flex gap-3"
						onSubmit={(event) => {
							event.preventDefault();
							const trimmed = userId.trim();
							if (!UUID_PATTERN.test(trimmed)) {
								setLookupFormatError(
									"Enter the user's id (UUID), not a phone number.",
								);
								setLookedUpUserId("");
								return;
							}
							setLookupFormatError(null);
							setLookedUpUserId(trimmed);
						}}
					>
						<Input
							placeholder="User id"
							value={userId}
							onChange={(event) => {
								setUserId(event.target.value);
								setLookupFormatError(null);
							}}
						/>
						<Button type="submit" disabled={!userId.trim()}>
							Look up
						</Button>
					</form>

					{lookupFormatError ? (
						<p role="alert" className="text-sm text-red-700">
							{lookupFormatError}
						</p>
					) : null}

					{userRolesQuery.isLoading ? (
						<Skeleton className="h-32 w-full rounded-2xl" />
					) : userRolesQuery.isError ? (
						<EmptyState
							title={
								isBadRequestError(userRolesQuery.error)
									? "Invalid user id. Double-check it and try again."
									: "Could not load this user's roles. Please try again."
							}
						/>
					) : lookedUpUserId && !assignment ? (
						<EmptyState title="No user matches that id." />
					) : assignment ? (
						<div className="grid gap-4">
							<div>
								<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
									Operator
								</p>
								<p className="font-mono text-sm">
									{assignment.user.phone ?? assignment.user.id}
								</p>
							</div>

							<div className="grid gap-2">
								<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
									Roles held
								</p>
								{assignment.roles.length === 0 ? (
									<p className="text-sm text-muted-foreground">None.</p>
								) : (
									<ul className="grid gap-2">
										{assignment.roles.map((role) => (
											<li
												key={role.id}
												className="flex items-center justify-between rounded-xl border px-4 py-2"
											>
												<span className="font-medium">{role.name}</span>
												{canWrite ? (
													<Button
														variant="outline"
														size="sm"
														disabled={revokeMutation.isPending}
														onClick={() =>
															revokeMutation.mutate({ roleId: role.id })
														}
													>
														Revoke
													</Button>
												) : null}
											</li>
										))}
									</ul>
								)}
							</div>

							{canWrite ? (
								<div className="grid gap-2">
									<p className="text-xs uppercase tracking-[0.14em] text-muted-foreground">
										Grant a role
									</p>
									<div className="flex gap-3">
										<Select value={roleToGrant} onValueChange={setRoleToGrant}>
											<SelectTrigger aria-label="Role to grant">
												<SelectValue placeholder="Select a role" />
											</SelectTrigger>
											<SelectContent>
												{grantableRoles.map((role) => (
													<SelectItem key={role.id} value={role.id}>
														{role.name}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<Button
											disabled={!roleToGrant || grantMutation.isPending}
											onClick={() =>
												grantMutation.mutate({ roleId: roleToGrant })
											}
										>
											Grant
										</Button>
									</div>
								</div>
							) : null}
						</div>
					) : null}
				</CardContent>
			</Card>
		</div>
	);
}
